import type { SpeechConfig } from '@omni/services/authentication.service';
import { CustomEndpoint, resolveRecogniser } from './recogniser-choice';
import { speechLocaleForLcid } from './speech-locale';

/** Page size of the endpoint listing. */
const ENDPOINT_PAGE_SIZE = 100;

/** Pages of the endpoint listing followed before the listing is taken as complete. */
const MAX_ENDPOINT_PAGES = 20;

/** Azure region names are lower-case letters and digits, e.g. `eastus`, `westeurope`. */
const REGION_PATTERN = /^[a-z0-9]+$/i;

/** The Speech resource a tenant's dictation runs against. */
export interface SpeechResource {
  region: string;
  subscriptionKey: string;
}

/** What a Speech resource reported about itself, as kept on the device. */
export interface EndpointCatalogue {
  /** The resource the catalogue describes, as {@link resourceIdentity} names it. */
  resource: string;
  /** Every custom endpoint on the resource. */
  endpoints: CustomEndpoint[];
  /** Locales the resource transcribes, or `null` when the service did not report them. */
  serviceLocales: string[] | null;
  /** ISO 8601 time of the fetch. */
  fetchedAt: string;
}

/** Everything a call site needs to configure the Speech SDK for one dictation. */
export interface SpeechRecogniser {
  subscriptionKey: string;
  region: string;
  /** BCP-47 recognition language. */
  language: string;
  /** Custom endpoint id. Absent when the base service is used. */
  endpointId?: string;
}

/** Reads a URL of the Speech REST API; resolves to the parsed JSON body and rejects on any failure. */
export type SpeechApiGet = (url: string) => Promise<unknown>;

/** Where the catalogue is kept between app sessions. */
export interface CatalogueStorage {
  read(): Promise<EndpointCatalogue | null | undefined>;
  write(catalogue: EndpointCatalogue): Promise<void>;
}

/** The speech resource a configuration names, or `null` when it names none. */
export function speechResourceOf(config: SpeechConfig | null | undefined): SpeechResource | null {
  const subscriptionKey = config?.speechSubscriptionKey?.trim();
  const region = config?.speechRegion?.trim();
  return subscriptionKey && region ? { region, subscriptionKey } : null;
}

/**
 * A stable name for a Speech resource: its region and a fingerprint of its key.
 * The key itself is never part of the name, so the name can be stored.
 */
export function resourceIdentity(resource: SpeechResource): string {
  return `${resource.region.trim().toLowerCase()}:${fingerprint(resource.subscriptionKey.trim())}`;
}

/** FNV-1a 32-bit hash as hexadecimal. Detects a changed key; not a security measure. */
function fingerprint(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Reads one page of `GET speechtotext/v3.2/endpoints`. Entries without an id,
 * locale or status are skipped.
 */
export function readEndpointPage(body: unknown): { endpoints: CustomEndpoint[]; nextLink?: string } {
  const page = (body ?? {}) as { values?: unknown; '@nextLink'?: unknown };
  const values = Array.isArray(page.values) ? page.values : [];
  const endpoints = values
    .map((value) => toEndpoint(value))
    .filter((endpoint): endpoint is CustomEndpoint => endpoint !== null);
  const nextLink = typeof page['@nextLink'] === 'string' && page['@nextLink'] ? page['@nextLink'] : undefined;
  return nextLink ? { endpoints, nextLink } : { endpoints };
}

/** An endpoint entry as the Speech REST API lists it; every field is checked before use. */
interface ListedEndpoint {
  self?: unknown;
  locale?: unknown;
  status?: unknown;
  createdDateTime?: unknown;
}

function toEndpoint(value: unknown): CustomEndpoint | null {
  const entry = (value ?? {}) as ListedEndpoint;
  const id = typeof entry.self === 'string' ? entry.self.split('?')[0].split('/').pop() : undefined;
  const { locale, status } = entry;
  if (!id || typeof locale !== 'string' || !locale || typeof status !== 'string') {
    return null;
  }
  const createdDateTime = typeof entry.createdDateTime === 'string' ? entry.createdDateTime : '';
  return { id, locale, status, createdDateTime };
}

/**
 * Fetches the catalogue of a Speech resource: every custom endpoint, following
 * `@nextLink`, and the locales the resource transcribes.
 *
 * @throws When the region is not an Azure region name (the key is then never sent),
 *   or when the endpoint listing fails. A failed locale listing is reported as `null`.
 */
export async function fetchEndpointCatalogue(
  resource: SpeechResource,
  get: SpeechApiGet,
  now: () => Date = () => new Date(),
): Promise<EndpointCatalogue> {
  const region = resource.region.trim();
  if (!REGION_PATTERN.test(region)) {
    throw new Error('The speech region is not an Azure region name.');
  }
  const api = `https://${region.toLowerCase()}.api.cognitive.microsoft.com/speechtotext/v3.2`;
  const [endpoints, serviceLocales] = await Promise.all([
    listEndpoints(`${api}/endpoints?skip=0&top=${ENDPOINT_PAGE_SIZE}`, get),
    listLocales(`${api}/transcriptions/locales`, get),
  ]);
  return { resource: resourceIdentity(resource), endpoints, serviceLocales, fetchedAt: now().toISOString() };
}

async function listEndpoints(firstPage: string, get: SpeechApiGet): Promise<CustomEndpoint[]> {
  const endpoints: CustomEndpoint[] = [];
  let url: string | undefined = firstPage;
  for (let pages = 0; url && pages < MAX_ENDPOINT_PAGES; pages++) {
    const page = readEndpointPage(await get(url));
    endpoints.push(...page.endpoints);
    url = page.nextLink;
  }
  return endpoints;
}

async function listLocales(url: string, get: SpeechApiGet): Promise<string[] | null> {
  try {
    const body = await get(url);
    return Array.isArray(body) ? body.filter((locale): locale is string => typeof locale === 'string' && !!locale) : null;
  } catch {
    return null;
  }
}

/**
 * The endpoints to choose among. Without a catalogue, the configured endpoint is
 * assumed to serve the configured language, which is how the tenant set it up.
 */
export function knownEndpoints(catalogue: EndpointCatalogue | null | undefined, config: SpeechConfig | null | undefined): CustomEndpoint[] {
  if (catalogue) {
    return catalogue.endpoints;
  }
  const id = config?.speechCustomEndpointId?.trim();
  const locale = config?.speechLanguage?.trim();
  return id && locale ? [{ id, locale, status: 'Unknown', createdDateTime: '' }] : [];
}

/**
 * The recogniser for a rep, from the tenant's speech configuration, the LCID of
 * the language the rep has selected in the app and the resource catalogue.
 *
 * @returns `null` when no speech resource is configured, or when no recogniser serves the rep's language.
 */
export function recogniserFor(
  config: SpeechConfig | null | undefined,
  lcid: string | number | null | undefined,
  catalogue: EndpointCatalogue | null | undefined,
): SpeechRecogniser | null {
  const resource = speechResourceOf(config);
  if (!resource) {
    return null;
  }
  const choice = resolveRecogniser(
    speechLocaleForLcid(lcid),
    config?.speechCustomEndpointId,
    knownEndpoints(catalogue, config),
    catalogue?.serviceLocales,
  );
  return choice ? { ...resource, ...choice } : null;
}

/**
 * The catalogue of the tenant's Speech resource, held in memory and in device storage.
 *
 * Fetched once per app session for each resource, so a changed key or region is
 * fetched afresh, and again after {@link invalidate}. Offline, or when a fetch
 * fails, the stored catalogue of the same resource is used. Everything else is
 * served from memory, so choosing again for another language costs no request.
 */
export class EndpointCatalogueCache {
  #held: EndpointCatalogue | null = null;
  readonly #attempted = new Set<string>();
  readonly #loading = new Map<string, Promise<EndpointCatalogue | null>>();

  constructor(
    private readonly storage: CatalogueStorage,
    private readonly fetchCatalogue: (resource: SpeechResource) => Promise<EndpointCatalogue>,
    private readonly isOnline: () => boolean,
  ) {}

  /**
   * The catalogue for a resource, or `null` when nothing is known about it.
   * Concurrent calls share one fetch. Never rejects.
   */
  public get(resource: SpeechResource): Promise<EndpointCatalogue | null> {
    const identity = resourceIdentity(resource);
    const inFlight = this.#loading.get(identity);
    if (inFlight) {
      return inFlight;
    }
    const loading = this.#load(resource, identity).finally(() => this.#loading.delete(identity));
    this.#loading.set(identity, loading);
    return loading;
  }

  /** Fetches afresh on the next {@link get}; used when a recognition on a custom endpoint was cancelled. */
  public invalidate(): void {
    this.#attempted.clear();
  }

  async #load(resource: SpeechResource, identity: string): Promise<EndpointCatalogue | null> {
    if (this.#held?.resource !== identity) {
      this.#held = await this.#stored(identity);
    }
    if (!this.isOnline() || this.#attempted.has(identity)) {
      return this.#held;
    }
    this.#attempted.add(identity);
    try {
      this.#held = await this.fetchCatalogue(resource);
    } catch (error) {
      console.warn('Speech endpoint discovery failed; the stored catalogue is used.', (error as Error)?.message ?? error);
      return this.#held;
    }
    try {
      await this.storage.write(this.#held);
    } catch (error) {
      console.warn('Speech endpoint catalogue not stored.', (error as Error)?.message ?? error);
    }
    return this.#held;
  }

  async #stored(identity: string): Promise<EndpointCatalogue | null> {
    try {
      const stored = await this.storage.read();
      return stored?.resource === identity ? stored : null;
    } catch {
      return null;
    }
  }
}
